<template>
  <div class="smart-combobox" v-click-outside="outside">
    <v-icon :color="'#DA4453'" :font-size="'2em'" class="icon-left">{{ icon }}</v-icon>
    <div class="container">
      <span class="label">{{ label }}</span>
      <input
        :disabled="!hasItems"
        :placeholder="placeholder"
        @click="showList = true"
        @keydown.down.prevent="navigate(1)"
        @keydown.up.prevent="navigate(-1)"
        @keyup.8="handleBackspace"
        @keyup.enter.prevent="select(selectedIndex)"
        class="input"
        type="text"
        v-model="search"/>
      <v-icon
        @click="onResetClick()"
        class="icon"
        h="0.8em"
        v-if="search">
        x
      </v-icon>
      <little-spinner v-if="withLoader && !hasItems"/>
    </div>
    <ul class="list" ref="container" v-if="showList">
      <li :class="{'selected': id === selectedIndex}"
          :key="id"
          @click="select(id)"
          class="list-item"
          ref="items"
          v-for="(item, id) in filteredItems"
          v-if="hasItems">
        {{ item.toUpperCase() }}
      </li>
    </ul>
  </div>
</template>

<script>

  export default {
    name: 'SmartCombobox',
    props: {
      placeholder: {
        type: String,
        default: ''
      },

      items: {
        type: Array,
        default: () => []
      },

      value: {
        type: String,
        default: ''
      },

      label: {
        type: String,
        default: ''
      },

      icon: {
        type: String,
        default: 'pin'
      },

      withLoader: {
        type: Boolean,
        default: true
      }
    },

    data() {
      return {
        showList: false,
        selectedIndex: 0,
        search: ''
      }
    },

    watch: {
      search(newValue, oldValue) {
        this.selectedIndex = 0
      },

      value: {
        immediate: true,
        handler(value) {
          this.search = value ? value : ''
        }
      }
    },

    computed: {
      hasItems() {
        return this.items && this.items.length > 0
      },

      filteredItems() {
        return this.hasItems ? this.items.filter(i => i.toLowerCase().includes(this.search.toLowerCase())) : []
      }
    },

    methods: {
      outside() {
        this.showList = false
      },

      handleBackspace() {
        this.showList = true
      },

      select(index) {
        const clickedItem = this.filteredItems[index]
        this.showList = false
        this.selectedIndex = index
        this.search = clickedItem
        this.$emit('select', this.items.findIndex(item => item === clickedItem))
      },

      navigate(direction) {
        if (this.showList) {
          this.selectedIndex = (this.selectedIndex + direction) % this.filteredItems.length
          if (this.selectedIndex < 0) {
            this.selectedIndex = this.filteredItems.length - 1
          }

          this.autoScroll()
        } else this.showList = true
      },

      autoScroll() {
        const container = {
          el: this.$refs.container,
          ...this.$refs.container.getBoundingClientRect().toJSON()
        }

        const child = {
          el: this.$refs.items[this.selectedIndex],
          ...this.$refs.items[this.selectedIndex].getBoundingClientRect().toJSON()
        }

        if (!(child.bottom < container.bottom && child.y > container.y)) {
          container.el.scrollTop = child.el.offsetTop
        }
      },

      onResetClick() {
        this.search = ''
        this.$emit('reset')
      }
    }
  }
</script>

<style lang="scss" scoped>
  .smart-combobox {
    height: 100%;
    flex: 1;
    margin-right: 2rem;
    border-radius: 5px 5px 0 0;
    background-color: #FFFFFF;
    max-height: 80px;
    padding: 1rem;
    position: relative;
    @include display-flex(center, flex-start, row);

    .icon-left {
      margin-right: 1rem;
    }

    .container {
      width: 100%;
      height: 100%;
      position: relative;
      @include display-flex(flex-start, space-between, column);

      .label {
        color: $light-red;
        font: $secondary-font;
        font-weight: 600;
      }

      .input {
        font-size: 14px;
        border: 0;
        background: #FFFFFF;
        width: 90%;
        color: $text-extra-lightgrey;
        font-family: $secondary-font;

        &:focus {
          box-shadow: none;
          -webkit-appearance: none;
          outline: 0;
        }
      }

      .icon {
        position: absolute;
        bottom: -2px;
        right: 2px;
      }

      .little-spinner {
        right: 30px;
        bottom: -5px;
        position: absolute;
      }
    }

    .list {
      background: $app-background;
      padding: 0;
      overflow-y: scroll;
      -webkit-box-shadow: 0 0 8px 1px rgba(0, 0, 0, 0.40);
      box-shadow: 0 0 8px 1px rgba(0, 0, 0, 0.40);
      position: absolute;
      top: 100%;
      width: 100%;
      max-height: 300px;
      z-index: 2;
      left: 0;
      margin-top: 0.5rem;

      &::-webkit-scrollbar {
        display: none;
      }

      &-item {
        font-family: $text-font-stack;
        font-weight: 400;
        font-size: 14px;
        color: $text-lightgrey;
        margin: 0;
        list-style-type: none;
        cursor: pointer;
        overflow: hidden;
        white-space: nowrap;
        text-align: left;
        padding: 8px 150px 8px 16px;
        height: 48px;
        @include display-flex(center, flex-start, row);

        &:hover {
          background: rgba(0, 0, 0, 0.04);
        }

        &.selected {
          color: $app-background;
          background-color: $light-red;
        }
      }
    }
  }

</style>
